import { useState } from "react";
import { formatNoteTime } from "../DeliverySections";
import { NOTE_FOLD_CHARS, noteParagraphs, type EditRequestSurface, type NoteChipState } from "../staff-feedback";
import { VOICE_NOTE_KEYS, voiceNoteState, type VoiceNoteState } from "../voice-note";

// ONE way a dictated note is set, on every staff list that shows one. A
// four-thousand-character dictation used to render as a single paragraph
// (audit N03): its own paragraph breaks were thrown away, and the resolve
// control sat 730 px above the last word. Paragraphs come back, and anything
// past NOTE_FOLD_CHARS opens on request instead of burying the page.
export function StaffNoteText(props: { text: string; t: (key: string) => string }) {
  const [open, setOpen] = useState(false);
  const paragraphs = noteParagraphs(props.text);
  const long = props.text.length > NOTE_FOLD_CHARS;
  const shown = long && !open
    ? (() => {
        const head: string[] = [];
        let budget = NOTE_FOLD_CHARS;
        for (const paragraph of paragraphs) {
          if (budget <= 0) break;
          head.push(paragraph.length > budget ? `${paragraph.slice(0, budget).trimEnd()}…` : paragraph);
          budget -= paragraph.length;
        }
        return head;
      })()
    : paragraphs;
  return (
    <div className="doc-note-body" data-note-folded={long && !open ? "true" : "false"}>
      {shown.map((paragraph, index) => (
        <p className="doc-note-text" key={index}>{paragraph}</p>
      ))}
      {long && (
        <button className="quiet-link doc-note-more" type="button" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
          {props.t(open ? "staffConsole.material.noteCollapse" : "staffConsole.material.noteExpand")}
        </button>
      )}
    </div>
  );
}
// Exported for the markup guard in tests/staff-feedback-pane.test.ts.
// Shared voice-note fallback for both staff note lists: name the real state
// (transcribing / no speech / failed) and let the partner play the original
// recording instead of a dead "Hlasová poznámka" label (night walk R22).
export function StaffVoiceNote({ text, audioUrl, transcriptStatus, t }: {
  text: string | null;
  audioUrl: string | null;
  transcriptStatus: string | null;
  t: (key: string) => string;
}) {
  const state = voiceNoteState({ text, hasAudio: audioUrl !== null, transcriptStatus });
  return (
    <>
      <p className="doc-note-text doc-note-voice">{t(VOICE_NOTE_KEYS[state as Exclude<VoiceNoteState, "text">])}</p>
      {audioUrl && <audio className="doc-note-audio" controls preload="none" src={audioUrl} aria-label={t("sales.notes.voicePlay")} />}
    </>
  );
}

export function staffAudioUrl(clientId: string, item: Record<string, unknown>): string | null {
  return typeof item.audioAttachmentId === "string" && item.audioAttachmentId
    ? `/api/staff/clients/${clientId}/attachments/${item.audioAttachmentId}`
    : null;
}

// ONE chip element for every staff note card (queue pane and material room):
// the four lifecycle states carry their own color and, where the request
// knows it, the exact time the state started (tooltip). Exported for tests.
export const STAFF_CHIP_CLASS: Record<NoteChipState, string> = {
  new: "is-new",
  sent: "is-sent",
  working: "is-working",
  handled: "is-handled",
  dismissed: "is-dismissed"
};

export function StaffNoteChip(props: { state: NoteChipState; t: (key: string) => string; title?: string }) {
  return (
    <span className={`note-state-chip ${STAFF_CHIP_CLASS[props.state]}`} title={props.title}>
      {props.t(`staffConsole.material.noteState.${props.state}`)}
    </span>
  );
}

// Tooltip truth per chip: when was it sent, when did Alex start.
export function staffChipTitle(
  chip: NoteChipState,
  surface: EditRequestSurface | null,
  t: (key: string) => string
): string | undefined {
  if (!surface) return undefined;
  if (chip === "sent" && surface.requestedAt) {
    return t("staffConsole.material.sentAt").replace("{time}", formatNoteTime(surface.requestedAt));
  }
  if (chip === "working" && surface.servedAt) {
    return t("staffConsole.material.workingSince").replace("{time}", formatNoteTime(surface.servedAt));
  }
  return undefined;
}
